/**
 * Physical Therapy Evaluation Note — options + empty payload.
 * After shared common header: Living Situation → Subjective → Objective →
 * Assessment → Plan of Care (per product screenshots). No wound section.
 */

import { emptyPtNotesCommonHeader } from "./ptNotesCommonHeaderUtils";
import { emptyTextGoals } from "./ptGoals";

export { PAIN_LEVELS } from "./ptClinicalVitals";

export const LIVES_WITH_OPTIONS = [
  "Alone",
  "Spouse",
  "Family",
  "Caregiver",
  "Other",
];

export const RESIDENCE_TYPE_OPTIONS = [
  "Private Home",
  "Apartment",
  "Assisted Living",
  "Other",
];

export const STAIRS_OPTIONS = ["None", "1–3 steps", "4–6 steps", "7+ steps", "Unknown"];

export const ENTRANCE_OPTIONS = ["Level", "Ramp", "Stairs", "Other"];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const GAIT_AID_OPTIONS = ["None", "SPC", "Walker", "W/C", "Other"];

export const SKIN_INTEGRITY_OPTIONS = ["Intact", "Impaired"];

export const BALANCE_STATUS_OPTIONS = ["Good", "Fair", "Poor"];

export const FALL_RISK_OPTIONS = ["LOW", "MOD", "HIGH"];

export const REHAB_POTENTIAL_OPTIONS = ["Excellent", "Good", "Fair", "Poor"];

export const BARRIERS_OPTIONS = ["Cognitive", "Physical", "Social", "None"];

export const BED_MOBILITY_OPTIONS = [
  "Independent",
  "Modified Independent",
  "Supervision",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Dependent",
];

export const TRANSFER_OPTIONS = [
  "Independent",
  "Modified Independent",
  "Contact Guard",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Dependent",
];

export const AMBULATION_OPTIONS = [
  "Independent",
  "With Device (Supervision)",
  "With Device (CGA)",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Non-ambulatory",
];

export const ASSISTIVE_DEVICE_OPTIONS = [
  "None",
  "SPC",
  "Quad Cane",
  "FWW",
  "4WW",
  "Rollator",
  "Wheelchair",
  "Other",
];

export const PT_INTERVENTION_OPTIONS = [
  "Therapeutic Exercise (97110)",
  "Neuromuscular Re-education (97112)",
  "Gait Training (97116)",
  "Transfer Training (97530)",
  "Balance Training (97703)",
  "Therapeutic Activities (97530A)",
  "Patient/Caregiver Education",
  "Home Exercise Program",
  "Modalities",
  "Other",
];

/** Strength & ROM joint rows — fixed clinical precision grid. */
export const STRENGTH_ROM_JOINTS = [
  "Hip Flexion",
  "Hip Extension",
  "Hip Abduction",
  "Knee Flexion",
  "Knee Extension",
  "Ankle Dorsiflexion",
  "Ankle Plantarflexion",
  "Inversion",
  "Eversion",
  "Great Toe Extension",
];

export const emptyStrengthRomRow = (joint) => ({
  joint,
  mmtRight: "",
  mmtLeft: "",
  romRight: "",
  romLeft: "",
});

export const emptyStrengthRomRows = () =>
  STRENGTH_ROM_JOINTS.map((joint) => emptyStrengthRomRow(joint));

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

/** Full editable PT Evaluation payload (JSON on dedicated row). */
export const emptyPtEvalPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  commonHeader: emptyPtNotesCommonHeader(),

  /** Onset / surgical (clinical, not duplicate of common diagnosis list). */
  onsetDate: "",
  surgicalDate: "",

  // —— Living Situation ——
  livesWith: "",
  livesWithOther: "",
  residenceType: "",
  residenceTypeOther: "",
  stairs: "",
  railing: "",
  entrance: "",

  // —— Subjective / History ——
  plof: "",
  currentLevelOfFunction: "",
  chiefComplaint: "",
  historyPresentIllness: "",
  medicationHistory: "",
  allergiesNarrative: "",
  currentMedicationsNarrative: "",
  relievingFactors: "",
  aggravatingFactors: "",
  painLevel: null,

  // —— Objective: Vitals ——
  vitalsBp: "",
  vitalsHr: "",
  vitalsSpo2: "",
  vitalsRr: "",
  vitalsTemp: "",

  // —— Objective: Strength & ROM ——
  strengthRom: emptyStrengthRomRows(),

  // —— Objective: Functional mobility ——
  bedMobility: "",
  transfers: "",
  ambulation: "",
  ambulationDistanceFt: "",
  assistiveDevice: "",

  // —— Objective: Observation ——
  posture: "",
  gaitAid: "",
  skinIntegrity: "",

  // —— Objective: Balance ——
  balanceStatus: "",
  tinettiScore: "",
  bergBalance: "",

  // —— Objective: Special tests ——
  tugSec: "",
  tenMeterWalk: "",
  fiveXSitToStand: "",
  specialTestOther: "",

  // —— Objective: Fall risk ——
  fallRisk: "",
  fallRiskComments: "",

  // —— Assessment ——
  clinicalSummary: "",
  rehabPotential: "",
  barriersToProgress: [],

  // —— Plan of Care ——
  skilledPtIndicated: "",
  frequency: "",
  anticipatedDischargeDate: "",
  interventions: [],
  interventionOther: "",
  shortTermGoals: emptyTextGoals(1, "st"),
  longTermGoals: emptyTextGoals(1, "lt"),
});

/** Array keys that must stay arrays after load/merge. */
export const PT_EVAL_ARRAY_KEYS = ["barriersToProgress", "interventions"];

/** Coerce legacy array choice → single string (Lived With / Residence). */
export const coerceExclusiveChoice = (value) => {
  if (Array.isArray(value)) return value.find(Boolean) || "";
  return value == null ? "" : String(value);
};
